import blogFallback from '@/assets/blog-fallback.jpg';

// vicorpus.com / vicorpus.co are legacy hosts that no longer serve uploads
// (they return a 200 HTML page or 404), so we fall back when we see them.
const BROKEN_DOMAINS = ['vicorpus.com', 'vicorpus.co'];
const MIRRORED_IMAGE_PATH_ROUTE = '/api/public/product-image';
const MIRRORED_IMAGE_ROUTE = '/api/public/product-image-file';
const MIRRORED_IMAGE_FILE_ROUTE = '/api/public/product-image-file.jpg';

function storageKeyFromWpUrl(srcUrl: string): string | null {
  try {
    const url = new URL(srcUrl);
    if (url.hostname !== 'checkout.mypeptideco.com') return null;

    const safePath = url.pathname
      .split('/')
      .map((segment) => segment.replace(/[^a-zA-Z0-9._-]/g, '_'))
      .join('/')
      .replace(/^\/+/, '')
      .replace(/\/+$/, '')
      .replace(/^wp-content\/uploads\//, '');

    if (!safePath) return null;
    return safePath;
  } catch {
    return null;
  }
}

function stripLegacyHostPrefix(key: string): string {
  return key
    .replace(/^\/+|\/+$/g, '')
    .replace(/^checkout\.mypeptideco\.com\//i, '')
    .replace(/^checkout_mypeptideco_com\//i, '');
}

function productImageFileUrl(key: string): string {
  const cleanKey = stripLegacyHostPrefix(key);
  const encodedKey = cleanKey
    .split('/')
    .map((segment) => encodeURIComponent(segment))
    .join('/');

  return `${MIRRORED_IMAGE_PATH_ROUTE}/${encodedKey}`;
}

export const BLOG_FALLBACK_IMAGE = blogFallback;

export function getProxiedImageUrl(originalUrl: string): string {
  if (!originalUrl) return originalUrl;
  const marker = '/api/public/product-image/';
  const markerIndex = originalUrl.indexOf(marker);

  if (originalUrl.includes(`${MIRRORED_IMAGE_FILE_ROUTE}?`)) {
    const key = new URL(originalUrl, 'https://www.mypeptideco.com').searchParams.get('key');
    if (key) return productImageFileUrl(key);
  }

  if (originalUrl.includes(`${MIRRORED_IMAGE_ROUTE}?`)) {
    const key = new URL(originalUrl, 'https://www.mypeptideco.com').searchParams.get('key');
    if (key) return productImageFileUrl(key);
  }

  if (markerIndex >= 0) {
    const rawKey = originalUrl
      .slice(markerIndex + marker.length)
      .split(/[?#]/)[0]
      .replace(/^\/+|\/+$/g, '');

    if (rawKey) {
      return productImageFileUrl(rawKey);
    }
  }

  const wpStorageKey = storageKeyFromWpUrl(originalUrl);
  if (wpStorageKey) return productImageFileUrl(wpStorageKey);

  return originalUrl;
}

export function getBlogImageUrl(originalUrl: string | null | undefined): string {
  if (!originalUrl) return blogFallback;
  if (BROKEN_DOMAINS.some(d => originalUrl.includes(d))) return blogFallback;
  return getProxiedImageUrl(originalUrl);
}

export function isProxiedUrl(url: string): boolean {
  return url.includes('/functions/v1/image-proxy') || url.includes('/api/public/product-image');
}

// Requests a resized variant of a mirrored product image. Non-mirrored URLs
// (local assets, fallbacks) are returned untouched.
export function getSizedImageUrl(url: string, width: number): string {
  if (!url || !url.includes('/api/public/product-image/')) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}w=${width}`;
}

export function getImageSrcSet(url: string, widths: number[] = [320, 480, 640, 960]): string | undefined {
  if (!url || !url.includes('/api/public/product-image/')) return undefined;
  return widths.map((w) => `${getSizedImageUrl(url, w)} ${w}w`).join(', ');
}
